import { useState, useEffect } from 'react';
import { toast } from 'react-toastify';

import axios from 'axios';
import config from '../../config';

import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';

import { FormControlLabel, Checkbox } from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';

const CreateGatePassForm = ({ property, unitOptions = [], account, role, onRequestCreated, onRequestUpdated, selectedRequest, onClose }) => {
    const isUnitObject = !!property?.property?._id;
    const [formData, setFormData] = useState({
        unit: isUnitObject ? property._id : '',
        requestedBy: account?.accountId || account,
        managedBy: [],
        assignees: [],
        type: 'GatePass',
        status: 'Pending',
        description: '',
        details: {
            title: '',
            firstName: '',
            lastName: '',
            organization: false,
            startDate: '',
            endDate: '',
        },
    });
    const [error, setError] = useState(null);

    useEffect(() => {
        console.log("Property changed:", property);

    }, [property]);

    useEffect(() => {
        if (selectedRequest) {
            setFormData((prevData) => ({
                ...prevData,
                unit: selectedRequest.unit || '',
                requestedBy: selectedRequest.requestedBy || '',
                managedBy: selectedRequest.managedBy || [],
                assignees: selectedRequest.assignees || [],
                type: selectedRequest.type || 'GatePass',
                status: selectedRequest.status || 'Pending',
                description: selectedRequest.description || '',
                details: {
                    ...prevData.details,
                    firstName: selectedRequest.details?.firstName || '',
                    lastName: selectedRequest.details?.lastName || '',
                    organization: selectedRequest.details?.organizer || false,
                    startDate: selectedRequest.details?.startDate || '',
                    endDate: selectedRequest.details?.endDate || '',
                    title: selectedRequest.details?.title || '',
                },
            }));
        }
    }, [selectedRequest]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        if (['firstName', 'lastName', 'organization', 'startDate', 'endDate'].includes(name)) {
            // Update formData.details
            setFormData((prevData) => ({
                ...prevData,
                details: {
                    ...prevData.details,
                    [name]: value,
                },
            }));
        } else {
            // Update top-level formData
            setFormData((prevData) => ({
                ...prevData,
                [name]: value,
            }));
        }
    };

    const handleSubmit = async () => {
        if (!formData.unit) {
            setError('Please select a unit.');
            return;
        }

        try {
            const method = selectedRequest ? 'put' : 'post';
            const url = selectedRequest
                ? `${config.API_URL}/v2/request/${selectedRequest._id}`
                : `${config.API_URL}/v2/request`;

            const unitId = isUnitObject
                ? property._id
                : formData.unit?._id || formData.unit?.value || formData.unit || '';

            const payload = {
                ...formData,
                unit: unitId,
            };

            const response = await axios[method](url, payload, {
                headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}`, 'Content-Type': 'application/json' }
            });

            if (response.status === 200 || response.status === 201) {
                if (selectedRequest) {
                    onRequestUpdated(response.data);
                    toast.success('Gate pass updated successfully!');
                } else {
                    onRequestCreated(response.data);
                    toast.success('Gate pass created successfully!');
                }
                onClose();
            } else {
                setError('Failed to save the gate pass.');
            }
        } catch (error) {
            setError(`Error ${selectedRequest ? 'updating' : 'creating'} the request. Please try again.`);
            toast.error(`Failed to ${selectedRequest ? 'update' : 'create'} the gate pass.`);
            console.error(error);
        }
    };

    return (
        <div className="popup-overlay">
            <div className="popup-content">
                <button className="close-popup" onClick={onClose}>
                    <i className="fas fa-times"></i>
                </button>
                <h2 className="text-2xl text-black">File a Gate Pass</h2>
                <div className="popup-columns">
                    <Autocomplete
                        options={unitOptions}
                        getOptionLabel={(option) => option.unitNumber || ''}
                        isOptionEqualToValue={(option, value) => option._id === value?._id}
                        value={isUnitObject ? { unitNumber: property?.unitNumber } : formData.unit || null}
                        onChange={(event, newValue) => {
                            if (!isUnitObject) {
                            setFormData((prevData) => ({
                                ...prevData,
                                unit: newValue || '',
                            }));
                            }
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Unit"
                                variant="outlined"
                                fullWidth
                                required
                            />
                        )}
                        disabled={isUnitObject}
                        sx={{ width: '100%', marginBottom: '1rem' }}
                    />
                </div>

                <TextField
                    label="Request Title"
                    name="title"
                    fullWidth
                    value={formData.details.title}
                    onChange={(event) => {
                        const newValue = event.target.value;
                        setFormData((prevData) => ({
                            ...prevData,
                            details: {
                                ...prevData.details,
                                title: newValue,
                            },
                        }));
                    }}
                    sx={{ marginBottom: '1rem' }}
                    required
                />

                {/* <Autocomplete
                    name="Filed by"
                    value={
                        [account].find((opt) => opt.accountId === formData.requestedBy) || null
                    }
                    onChange={(event, newValue) => {
                        if (newValue) {
                        setFormData((prevData) => ({
                            ...prevData,
                            requestedBy: newValue.accountId, // Store ID only
                        }));
                        }
                    }}
                    options={[account]} // Must be an array of account objects
                    getOptionLabel={(option) => option.accountName}
                    isOptionEqualToValue={(option, value) =>
                        option.accountId === value?.accountId
                    }
                    renderInput={(params) => (
                        <TextField {...params} label="Filed by" variant="outlined" fullWidth required />
                    )}
                    sx={{ width: '100%', marginBottom: '1rem' }}
                /> */}

                <div className="popup-columns">
                    <TextField
                        label="First Name"
                        name="firstName"
                        fullWidth
                        value={formData.details.firstName}
                        onChange={handleChange}
                        sx={{ marginBottom: '1rem' }}
                    />

                    <TextField
                        label="Last Name"
                        name="lastName"
                        fullWidth
                        value={formData.details.lastName}
                        onChange={handleChange}
                        sx={{ marginBottom: '1rem' }}
                    />
                </div>

                <FormControlLabel
                    control={
                        <Checkbox
                            checked={formData.details.organization}
                            onChange={(e) => {
                                setFormData((prevData) => ({
                                    ...prevData,
                                    details: {
                                        ...prevData.details,
                                        organization: e.target.checked,
                                    },
                                }));
                            }}
                            sx={{
                                '&.Mui-checked': {
                                    color: '#445485', // checked color
                                },
                            }}
                        />
                    }
                    label="Organization"
                    sx={{ marginBottom: '1rem' }}
                />

                {/* <Autocomplete
                    name="Type"
                    value={formData.type || ''}
                    onChange={(event, newValue) => {
                        if (newValue) {
                        setFormData((prevData) => ({
                            ...prevData,
                            type: newValue,
                        }));
                        }
                    }}
                    options={['GatePass']}
                    getOptionLabel={(option) => 'Gate Pass'}
                    renderInput={(params) => (
                        <TextField {...params} label="Type" variant="outlined" fullWidth required />
                    )}
                    sx={{ width: '100%', marginBottom: '1rem' }}
                /> */}

                <TextField
                    label="Description"
                    name="description"
                    multiline
                    fullWidth
                    rows={5}
                    value={formData.description}
                    onChange={handleChange}
                    sx={{ marginBottom: '1rem' }}
                />

                <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <div className="popup-columns">
                        <DatePicker
                            label="Start Date"
                            value={formData.details.startDate && dayjs(formData.details.startDate).isValid()
                                ? dayjs(formData.details.startDate) : null}
                            onChange={(newValue) => {
                                setFormData((prevData) => ({
                                    ...prevData,
                                    details: {
                                        ...prevData.details,
                                        startDate: newValue && dayjs(newValue).isValid() ? dayjs(newValue).toDate().toISOString() : null,
                                    },
                                }));
                            }}
                            slotProps={{ textField: { fullWidth: true, sx: { marginBottom: '1rem' } } }}
                        />

                        <DatePicker
                            label="End Date"
                            value={formData.details.endDate && dayjs(formData.details.endDate)
                                ? dayjs(formData.details.endDate) : null}
                            onChange={(newValue) => {
                                setFormData((prevData) => ({
                                    ...prevData,
                                    details: {
                                        ...prevData.details,
                                        endDate: newValue && dayjs(newValue).isValid() ? newValue.toISOString() : null,
                                    },
                                }));
                            }}
                            slotProps={{ textField: { fullWidth: true, sx: { marginBottom: '1rem' } } }}
                        />
                    </div>
                </LocalizationProvider>

                {role === 'manager' && selectedRequest && (
                    <Autocomplete
                        name="status"
                        value={formData.status || ''}
                        onChange={(event, newValue) => {
                            if (newValue) {
                                setFormData((prevData) => ({
                                    ...prevData,
                                    status: newValue,
                                }));
                            }
                        }}
                        options={['Pending', 'In-Progress', 'Completed', 'Rejected']}
                        renderInput={(params) => (
                            <TextField {...params} label="Status" variant="outlined" fullWidth required />
                        )}
                        sx={{ width: '100%', marginBottom: '1rem' }}
                    />
                )}

                <button type="button" className="button" onClick={handleSubmit}>
                    {selectedRequest ? 'Update Gate Pass' : 'File a Gate Pass'}
                </button>

                {error && <div className="error-message">{error}</div>}
            </div>
        </div>
    );
};

export default CreateGatePassForm;
